/**
 * Admin → Email
 *
 * One home for everything to do with sending e-mail. It used to be four separate
 * items in the Admin menu — Notifications, SMTP Settings, Email Templates and
 * Business Cards — which is most of why the owner described this part of the app
 * as clunky: nothing told you those four pages were the same subject, and
 * setting e-mail up meant finding all of them.
 *
 * The four pages are kept as components and rendered as tabs rather than being
 * rewritten into one. They are large, they work, and merging their internals
 * would risk their behaviour to gain nothing the tab bar does not already give.
 *
 * The active tab lives in the query string so a tab can be linked to, survives a
 * refresh, and gives the old routes somewhere to redirect *to* — `/admin/smtp`
 * becomes `/admin/email?tab=delivery` rather than a dead link.
 */
import { Navigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import AdminEmailTemplatesPage from './AdminEmailTemplatesPage'
import AdminBusinessCardsPage from './AdminBusinessCardsPage'
import NotificationSettingsPage from './NotificationSettingsPage'
import SmtpSettingsPage from './SmtpSettingsPage'
import AdminOutboxPage from './AdminOutboxPage'

/** Tab id → label. The id is the `?tab=` value, so it is part of the URL contract. */
export const EMAIL_TABS = [
  { id: 'templates', label: 'Messages' },
  { id: 'outbox', label: 'Outbox' },
  { id: 'signatures', label: 'Business Cards' },
  { id: 'reminders', label: 'Automatic Reminders' },
  { id: 'delivery', label: 'Delivery' },
] as const

type TabId = (typeof EMAIL_TABS)[number]['id']

const DEFAULT_TAB: TabId = 'templates'

function isTabId(value: string | null): value is TabId {
  return EMAIL_TABS.some((t) => t.id === value)
}

export default function AdminEmailPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const requested = searchParams.get('tab')
  // An unrecognised ?tab= falls back rather than rendering nothing — a stale
  // bookmark should land somewhere useful, not on a blank page.
  const active: TabId = isTabId(requested) ? requested : DEFAULT_TAB

  if (!user?.is_superuser) return <Navigate to="/dashboard" replace />

  function selectTab(id: TabId) {
    // `replace` so tabbing around does not fill the back button with tab changes.
    setSearchParams({ tab: id }, { replace: true })
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Email</h1>
        <p className="mt-1 text-sm text-gray-500">
          The messages you send, who they come from, what is queued to go out, when reminders
          are sent automatically, and the mail server that delivers them.
        </p>
      </div>

      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <nav className="flex gap-1 min-w-max" aria-label="Email settings">
          {EMAIL_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              aria-current={active === tab.id ? 'page' : undefined}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                active === tab.id
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {active === 'templates' && <AdminEmailTemplatesPage />}
      {active === 'outbox' && <AdminOutboxPage />}
      {active === 'signatures' && <AdminBusinessCardsPage />}
      {active === 'reminders' && <NotificationSettingsPage />}
      {active === 'delivery' && <SmtpSettingsPage />}
    </div>
  )
}
